import { LitElement, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';
import { db } from '../modules/db';
import { styles as commandPaletteStyles } from '../styles/command-palette';

@customElement('app-command-palette')
export class CommandPallete extends LitElement {
    @property({ type: Boolean }) active = true;

    static styles = [
        commandPaletteStyles,
    ];

    private activeCommand: Array = [];
    private activeIndex: number = 0;
    private commands = [
        { name: 'New Note', active: true, action: () => this.sendEvent({action: 'new-note'}) },
        { name: 'Modify Note..', action: () => this.setActiveCommand(1, true), children: [
            { name: 'Delete', active: true, action: () => this.sendEvent({action: 'delete-note'}) },
            { name: 'Duplicate', action: () => console.log('Duplicate Note') },
            { name: 'Move to Trash', action: () => console.log('Move to Trash') },
            { name: 'Restore from Trash', action: () => console.log('Restore from Trash') },
            { name: 'Export as Markdown', action: () => console.log('Export as Markdown') },
            { name: 'Export as PDF', action: () => console.log('Export as PDF') }
        ]  },
        {
            name: 'Insert..', action: () => this.setActiveCommand(2, true), children: [
                { name: 'H1', active: true, action: () => this.sendEvent({action: 'add-text', text: '# Heading 1'}) },
                { name: 'H2', action: () => this.sendEvent({action: 'add-text', text: '## Heading 2'}) },
                { name: 'H3', action: () =>  this.sendEvent({action: 'add-text', text: '### Heading 3'}) },
                { name: 'H4', action: () =>  this.sendEvent({action: 'add-text', text: '#### Heading 4'}) },
                { name: 'H5', action: () =>  this.sendEvent({action: 'add-text', text: '##### Heading 5'}) },
                { name: 'H6', action: () =>  this.sendEvent({action: 'add-text', text: '###### Heading 6'}) },
                { name: 'Table', action: () =>  this.sendEvent({action: 'add-text', text: '| A | B |\n|---|---|\n| C | D |'}) },
                { name: 'Code Block', action: () => this.sendEvent({action: 'add-text', text: '```\nCode Block\n```'}) },
                { name: 'Inline Code Block', action: () => this.sendEvent({action: 'add-text', text: '```Inline Code Block```'}) },
                { name: 'Quote', action: () => this.sendEvent({action: 'add-text', text: '> Quote'}) },
                { name: 'Horizontal Rule', action: () => this.sendEvent({action: 'add-text', text: '---'}) },
                { name: 'Image', action: () => this.sendEvent({action: 'add-text', text: '![Alt Text](https://place-hold.it/90&text=Image)'}) },
                { name: 'Link', action: () => this.sendEvent({action: 'add-text', text: '[Link Text](https://example.com)'}) },
            ]
        },
        { name: 'Folders..', action: () => this.setActiveCommand(3, true), children: []},
        { name: 'Toggle Preview', action: () => this.sendEvent({action: 'toggle-preview'}) },
        { name: 'Search Notes', action: () => console.log('Search Notes') }
        // Add more commands as needed
    ]

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('keydown', this.handleKeyNavigation);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('keydown', this.handleKeyNavigation);
    }

    handleKeyNavigation = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.active = false;
            this.activeIndex = 0;
            this.requestUpdate();

        } else if (e.key === 'ArrowDown' && this.active) {
            e.preventDefault();
            this.activeIndex++;
            this.activeCommand = this.activeCommand.map((command, index) => {
                command.active = index === this.activeIndex;
                return command;
            });
            this.requestUpdate();
        } else if (e.key === 'ArrowUp' && this.active) {
            this.activeIndex--;
            this.activeCommand = this.activeCommand.map((command, index) => {
                command.active = index === this.activeIndex;
                return command;
            });
            this.requestUpdate();
        } else if (e.key === 'Enter' && this.active) {
            e.preventDefault();
            if (this.activeCommand[this.activeIndex]) {
                const command = this.activeCommand[this.activeIndex];
                if (command.action) {
                    command.action();
                } else if (command.children) {
                    this.setActiveCommand(this.activeIndex, true);
                }

                this.activeIndex = 0;
            }
        }
    }

    sendEvent(detail: any) {
        let event = new CustomEvent('events', {
            bubbles: true,
            composed: true,
            detail: detail
        });

        this.dispatchEvent(event);
    }
    async setActiveCommand(index: number, children: boolean = false) {
        if (index < this.commands.length) {

            if(this.commands[index].name === 'Folders..') {
                // Fetch folders from the database and set them as children
                this.commands[index].children = [];
                await db.folders.toArray().then(folders => {
                    folders.forEach(folder => {
                        this.commands[index].children.push({
                            name: folder.name,
                            action: () => {
                                this.sendEvent({action: 'open-folder', folderName: folder.name});
                            }
                        });
                    });

                    this.requestUpdate();
                });
            }

            this.activeCommand = children
                ? [...(this.commands[index].children || [])]
                : [...this.commands];
            // add "go back" command to the the top of activeCommand
            if(children) {
                this.activeCommand.unshift({
                    name: html`<span style="font-size:1.2em;">&#8592;</span> Go Back`,
                    parent: index,
                    action: ((index) => {
                        return () => {
                            this.setActiveCommand(index);
                        }
                    })(index)
                });
            }

            this.activeCommand.forEach((command, i) => {
                command.active = i === 0; // Set the first command as active
            });

            // re render the component to update the UI
            this.requestUpdate();
        } else {
            this.activeCommand = [];
        }
    }

    render() {

        if (!this.activeCommand.length) {
            this.activeCommand = this.commands;
            this.activeIndex = 0;
        }

        return html`
    <section class="command-palette ${this.active ? 'active' : ''}">
        <div class="search-bar">
            <input type="text" placeholder="Search commands..." @input="${(e: InputEvent) => {
                const input = e.target as HTMLInputElement;
                this.dispatchEvent(new CustomEvent('search', {
                    detail: { query: input.value },
                    bubbles: true,
                    composed: true
                }));
            }}">
        </div>
        <div class="command-list">
            <ul>
                ${this.activeCommand.map(command => html`
                    <li class="${command?.active ? 'active' : ''}"  @click="${command.action}">
                        <a href="#">${command.name}</a>
                    </li>
                `)}
            </ul>
        </div>
    </section>
    `;
    }
}
